import { ChatMessages } from "@storytell/cp-client";
import type { StreamingBlockId, StreamingOperationId } from "@storytell/streaming";
import { type Component, Show, createMemo } from "solid-js";
import { ArtifactViewer } from "~/components/chat/ArtifactViewer";
import type { ArtifactState } from "~/stores/ArtifactStore";
import { useWire } from "~/wire";

interface ExtendedPaneArtifactViewerProps {
  operationId: StreamingOperationId;
  blockId?: StreamingBlockId; // Optional block ID for block-specific content
  title?: string;
}

export const ExtendedPaneArtifactViewer: Component<ExtendedPaneArtifactViewerProps> = (props) => {
  const wire = useWire();

  // Find artifact blocks in the operation, or specific block if blockId provided
  const artifactBlocks = createMemo(() => {
    const blocks = wire.chat
      .getOperationBlocks(props.operationId)
      ?.filter(
        (block) =>
          block.messageType === ChatMessages.MessageType.ARTIFACT_CREATION ||
          block.messageType === ChatMessages.MessageType.ARTIFACT_REVISION,
      );
    return blocks;
  });

  // Convert to ArtifactState format with streaming content
  const artifactState = createMemo((): ArtifactState | null => {
    const blocks = artifactBlocks();
    if (!blocks || blocks?.length === 0) return null;

    const firstBlock = blocks[0];

    // Always use "document" type since StreamingMarkdown can render all content types
    const artifactType = "document";

    return {
      id: firstBlock.blockId,
      title: props.title || firstBlock.title || firstBlock.activity || "Artifact",
      type: artifactType,
      content: "", // Use streaming content
      messageId: firstBlock.messageId,
      operationId: props.operationId,
      blockId: firstBlock.blockId,
      createdAt: new Date(),
      updatedAt: new Date(),
      isProcessing: !firstBlock.isComplete, // Use streaming processing state
      metadata: {
        // Pass through rich text data if available - DocumentArtifact will decide when to use it
        richText: firstBlock.finalContent?.type === "richText" ? firstBlock.finalContent.richText : undefined,
        originalBlocks: blocks.length, // Debug info
        streamingContentLength: 0, // Debug info
        // Block properties available: taskData, operationData, featureMetrics, etc.
        blockId: firstBlock.blockId,
        activity: firstBlock.activity,
        // status: firstBlock.stat,
      },
    };
  });

  return (
    <div data-testid="extended-pane-artifact-viewer" class="h-full">
      <Show
        when={artifactState()}
        fallback={
          <div class="h-full flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div class="text-center">
              <h3 class="text-lg font-medium mb-2">No Artifact Found</h3>
              <p class="text-sm">No artifact blocks found for this operation.</p>
            </div>
          </div>
        }
      >
        {(state) => <ArtifactViewer artifact={state()} isActive={true} class="h-full" />}
      </Show>
    </div>
  );
};
